import { Fragment, useMemo, useState } from 'react';
import {
  FaqBody, SiteFaqEntry, createSiteFaq, deleteSiteFaq, saveSiteFaq,
} from '../../../api/platform';
import { Modal, RowMenu, TableCard, Toggle } from '../../../components/fs';
import { PHONE, useMediaQuery } from '../../../shared/media';

/**
 * Site content › FAQ (the handoff's tab 3, R12's card unchanged): the public
 * FAQ in sort order, a category named where its group starts, a draft
 * question greyed; the row ⋯ (Edit… · Delete…) and the one delete confirm.
 * Saving is live at once. On a phone the card is a list of rows (ABC4-06).
 */

type Editor = { kind: 'faq'; id: number | null } | { kind: 'faq-delete'; id: number } | null;

/** A "+ New …" label whose accessible name is the words alone. */
const Plus = ({ children }: { children: string }) => <><span aria-hidden="true">+</span> {children}</>;

export default function FaqTab({ faq, readOnly, write }: {
  faq: SiteFaqEntry[] | null;
  readOnly: boolean;
  write: (fn: () => Promise<unknown>, done: string) => Promise<string | null>;
}) {
  const phone = useMediaQuery(PHONE);
  const [editor, setEditor] = useState<Editor>(null);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [error, setError] = useState('');
  const edit = (next: Editor) => { setError(''); setEditor(next); };
  const run = async (fn: () => Promise<unknown>, done: string) => {
    const refused = await write(fn, done);
    if (refused) setError(refused); else edit(null);
  };

  const editing = editor?.kind === 'faq' && editor.id !== null ? (faq || []).find((f) => f.id === editor.id) || null : null;
  const deleting = editor?.kind === 'faq-delete' ? (faq || []).find((f) => f.id === editor.id) || null : null;
  const categories = useMemo(() => [...new Set((faq || []).map((f) => f.category))], [faq]);

  const menu = (f: SiteFaqEntry) => !readOnly && (
    <RowMenu open={openMenu === f.id} onToggle={(open) => setOpenMenu(open ? f.id : null)} label={f.question}>
      <button onClick={() => edit({ kind: 'faq', id: f.id })}>Edit…</button>
      <div className="rule" />
      <button className="danger" onClick={() => edit({ kind: 'faq-delete', id: f.id })}>Delete…</button>
    </RowMenu>
  );
  const visibility = (f: SiteFaqEntry) => (f.published
    ? <span className="fs-pill ok">published</span>
    : <span className="fs-pill neutral">draft</span>);
  /** name the category only where a new group starts, like the page does */
  const startsGroup = (list: SiteFaqEntry[], i: number) => i === 0 || list[i - 1].category !== list[i].category;

  return (
    <>
      <TableCard
        className="sc-faq"
        title="FAQ"
        // Offered once the list is in: a new question is ordered after the last one.
        action={!readOnly && faq ? (
          <button type="button" className="fs-btn text" onClick={() => edit({ kind: 'faq', id: null })}>
            <Plus>New question</Plus>
          </button>
        ) : undefined}
        footer={(
          <span>
            Shown in sort order and grouped by category. In answers, a blank line starts a new paragraph and
            [label](/path) makes a link. Saving is live straight away.
          </span>
        )}
      >
        {!faq ? <div className="st-card-body st-muted">Loading…</div>
          : faq.length === 0 ? <div className="st-card-body st-muted st-empty">No questions yet.</div>
            : phone ? (
              <div className="sc-rows">
                {faq.map((f, i) => (
                  <Fragment key={f.id}>
                    {startsGroup(faq, i) && <div className="sc-group">{f.category}</div>}
                    <div className={`sc-row${f.published ? '' : ' sc-draft'}`}>
                      <div className="main">
                        <div className="title">{f.question}</div>
                        <div className="meta"><span className="sc-order">#{f.sort_order}</span> {visibility(f)}</div>
                      </div>
                      {menu(f)}
                    </div>
                  </Fragment>
                ))}
              </div>
            ) : (
              <table className="fs-table">
                <thead>
                  <tr>
                    <th className="sc-col-order">Order</th><th className="sc-col-category">Category</th><th>Question</th>
                    <th className="sc-col-vis">Visibility</th>
                    <th className="sc-col-menu fs-row-actions" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {faq.map((f, i) => (
                    <tr key={f.id} className={f.published ? undefined : 'sc-draft'}>
                      <td className="sc-order">{f.sort_order}</td>
                      <td>{startsGroup(faq, i) ? f.category : ''}</td>
                      <td className="sc-title">{f.question}</td>
                      <td>{visibility(f)}</td>
                      <td className="fs-row-actions">{menu(f)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </TableCard>

      {editor?.kind === 'faq' && (
        <FaqModal
          entry={editing}
          categories={categories}
          nextOrder={(faq || []).reduce((m, f) => Math.max(m, f.sort_order), 0) + 10}
          error={error}
          onClose={() => edit(null)}
          onSave={(body) => run(() => (editing ? saveSiteFaq(editing.id, body) : createSiteFaq(body)),
            body.published ? 'Saved — live on the public site now.' : 'Saved as a draft.')}
        />
      )}
      {deleting && (
        <Modal
          narrow
          title="Delete this question?"
          onClose={() => edit(null)}
          footer={(
            <>
              <button className="fs-btn danger solid" onClick={() => run(() => deleteSiteFaq(deleting.id), 'FAQ entry deleted.')}>
                Delete question
              </button>
              <button className="fs-btn neutral" onClick={() => edit(null)}>Cancel</button>
            </>
          )}
        >
          {error && <div className="ad-err">{error}</div>}
          <p className="sc-confirm">
            “{deleting.question}” disappears from the public FAQ immediately. To take it down
            without losing the text, edit it and untick “Published” instead.
          </p>
        </Modal>
      )}
    </>
  );
}

function FaqModal({ entry, categories, nextOrder, error, onClose, onSave }: {
  entry: SiteFaqEntry | null;
  categories: string[];
  nextOrder: number;
  /** the server's refusal of the last save */
  error: string;
  onClose: () => void;
  onSave: (body: FaqBody) => void;
}) {
  const [category, setCategory] = useState(entry?.category || categories[0] || '');
  const [question, setQuestion] = useState(entry?.question || '');
  const [answer, setAnswer] = useState(entry?.answer || '');
  const [sortOrder, setSortOrder] = useState(entry?.sort_order ?? nextOrder);
  const [published, setPublished] = useState(entry?.published ?? true);

  const valid = category.trim().length >= 2 && question.trim().length >= 4 && answer.trim();

  return (
    <Modal
      title={entry ? 'Edit question' : 'New question'}
      onClose={onClose}
      footer={(
        <>
          <span className="sc-foot-hint">{published ? 'Live on the public FAQ as soon as you save.' : 'Saved as a draft. Not shown on the site.'}</span>
          <button
            className="fs-btn primary"
            disabled={!valid}
            onClick={() => onSave({
              category: category.trim(), question: question.trim(), answer: answer.trim(),
              sort_order: sortOrder, published,
            })}
          >
            {entry ? 'Save question' : 'Add question'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="sc-form">
        {error && <div className="ad-err">{error}</div>}
        <div className="sc-faq-grid">
          <div className="fs-field">
            <label htmlFor="sc-faq-category">Category</label>
            <input id="sc-faq-category" type="text" value={category} list="faq-categories" onChange={(e) => setCategory(e.target.value)} />
            <datalist id="faq-categories">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
            <span className="hint">Pick an existing group, or type a new one to start it.</span>
          </div>
          <div className="fs-field">
            <label htmlFor="sc-faq-order">Sort order</label>
            <input id="sc-faq-order" type="number" value={sortOrder} onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)} />
          </div>
        </div>
        <div className="fs-field">
          <label htmlFor="sc-faq-question">Question</label>
          <input id="sc-faq-question" type="text" value={question} autoFocus={!entry} onChange={(e) => setQuestion(e.target.value)} />
        </div>
        <div className="fs-field">
          <label htmlFor="sc-faq-answer">Answer</label>
          <textarea id="sc-faq-answer" rows={7} value={answer} onChange={(e) => setAnswer(e.target.value)} />
          <span className="hint">Plain text. A blank line starts a new paragraph; [label](/pricing) makes a link.</span>
        </div>
        <Toggle checked={published} onChange={setPublished} label="Published — shown on the public FAQ page" />
      </div>
    </Modal>
  );
}
